import React from "react";
import { useDispatch } from "react-redux";

import {
  IgButton,
  IgHeaderWTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import { Close } from "@mui/icons-material";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";

import { shouldShowFilter } from "../../api/formatters/dashboard";
import {
  DashboardPageName,
  FilterDialogButtons,
} from "../../api/types/dashboards";
import { setDialog } from "../../store/slices/global";
import theme from "../../theme";

const PREFIX = "FilterDialog";

const classes = {
  saveButton: `${PREFIX}-saveButton`,
  clearButton: `${PREFIX}-clearButton`,
  sectionDivider: `${PREFIX}-sectionDivider`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.saveButton}`]: {
    textTransform: "none",
    fontSize: "14px",
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  [`& .${classes.clearButton}`]: {
    color: "rgba(0,0,0,.38)",
    textTransform: "none",
    fontSize: "14px",
    textDecoration: "underline",
  },
  [`& .${classes.sectionDivider}`]: {
    marginBottom: "30px",
    marginTop: "10px",
  },
}));

interface BaseFiltersDialogProps {
  isSaveLoading?: boolean;
  isSaveDisabled?: boolean;
  onFilterSave: () => void;
  openFilters: boolean;
  onFiltersClearAll: () => void;
  filterButtons: FilterDialogButtons[];
  pageName?: DashboardPageName;
}
export const BaseFiltersDialog = ({
  isSaveDisabled,
  onFilterSave,
  openFilters,
  isSaveLoading,
  onFiltersClearAll,
  pageName,
  filterButtons,
}: BaseFiltersDialogProps) => {
  const dispatch = useDispatch();
  return (
    <StyledDialog
      open={openFilters}
      onClose={() => {
        dispatch(setDialog({ dialog: "dashboardFilters", open: false }));
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{ style: { borderRadius: 15, minWidth: "600px" } }}
    >
      <DialogTitle>
        <Grid container direction={"row"} justifyContent={"space-between"}>
          <Grid item />
          <Grid item>
            <IgTypography>Filters</IgTypography>
          </Grid>
          <Grid item>
            <IconButton
              sx={{ padding: "0px", color: "rgba(0, 0, 0, 0.38)" }}
              size={"small"}
              onClick={() => {
                dispatch(
                  setDialog({ dialog: "dashboardFilters", open: false })
                );
              }}
            >
              <Close />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent dividers={true}>
        <Grid container direction={"column"} columnSpacing={1} rowSpacing={2}>
          {filterButtons.map(
            (filter: FilterDialogButtons, index: number) =>
              shouldShowFilter(filter, pageName) && (
                <Grid item key={filter.label}>
                  {index !== 0 && (
                    <Divider className={classes.sectionDivider} />
                  )}
                  <Grid
                    container
                    item
                    direction={"column"}
                    sx={{ marginBottom: "15px" }}
                  >
                    {filter.tooltip ? (
                      <IgHeaderWTooltip
                        sectionTitle={filter.label}
                        tooltipMessage={filter.tooltip}
                      />
                    ) : (
                      <IgTypography sx={{ fontSize: "16px", fontWeight: 500 }}>
                        {filter.label}
                      </IgTypography>
                    )}
                    {filter.settings.map((setting) => (
                      <Grid
                        container
                        item
                        direction={filter.isSwitch ? "row" : "column"}
                        key={setting.key}
                        alignItems={
                          filter.isSwitch && filter.hasDefault
                            ? "baseline"
                            : filter.isSwitch
                              ? "center"
                              : "normal"
                        }
                        justifyContent={"space-between"}
                      >
                        <Grid item sx={{ marginBottom: "10px" }}>
                          <IgTypography
                            sx={{
                              fontSize: "14px",
                              color: theme.palette.secondary.main,
                            }}
                          >
                            {setting.description}
                          </IgTypography>
                        </Grid>
                        <Grid item>{setting.component}</Grid>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              )
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Grid container direction={"row"} justifyContent={"space-between"}>
          <IgButton
            className={classes.clearButton}
            variant={"text"}
            size={"small"}
            onClick={onFiltersClearAll}
          >
            Reset
          </IgButton>
          <IgButton
            size={"small"}
            variant={"contained"}
            onClick={onFilterSave}
            loading={isSaveLoading}
            disabled={isSaveDisabled}
          >
            Save
          </IgButton>
        </Grid>
      </DialogActions>
    </StyledDialog>
  );
};
